import moment from "moment";
import React, { memo } from "react";
import { Field, Form } from "react-final-form";

import { InfoRounded as InfoIcon } from "@material-ui/icons";
import { Checkbox } from "@my-scoot/component-library-legacy";
import { ExlyDropdown, ExlyInput, ExlyOfferingSelect } from "common/form";
import ExlyPriceInput from "common/form/ExlyMoneyInput";
import { DurationPickerField, TooltipTitle } from "./components/CustomFields";
import LimitedOfferShare from "../../modules/LimitedOfferTimeShare";
import { is_desktop } from "utils/Utils";
import constants from "../../../constants/constants";
import {
  getUserCurrencySymbol,
  isInternationalCurrency,
  isInternationalTimezone,
} from "../../../utils/AuthUtil";
import { useStyles } from "./index.styles";
import style from "./Style.module.css";
import { useLimitedTimeOffer } from "./useLimitedTimeOffer";
import { required } from "common/validate";
import { composeValidators } from "utils/validations";
import { getUserTimezone } from "utils/AuthUtil";
import timezones from "constants/timezones/AllTimezonesArray";
import {
  validateOfferName,
  validateDiscountPrice,
  validatePercentage,
  validateEndDate,
  validateDiscountPriceInternational,
} from "./limitedOfferValidators";
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT_12,
} from "constants/dateTime";
import { ExlyNote } from "common/Components/ExlyNote/ExlyNote";
import { isInstallmentsPlanType } from "schedule-v2/commonPages/Components/PaymentPlanWithInstallments/utils/PaymentPlanWithInstallments.utils";
import { PART_PAYMENT_FORM_KEYS } from "schedule-v2/commonPages/Components/PaymentPlanWithInstallments/PaymentPlanWithInstallments.data";
import {
  LTO_FORM_KEYS,
  OFFER_TARGET_TYPE,
  OFFER_TARGET_TYPE_OPTIONS,
  OFFER_TYPE,
  OFFER_TYPE_OPTIONS,
  TIMER_TYPE,
  TIMER_TYPE_OPTIONS,
  DURATION_UNITS,
} from "./constants";
import CustomCodeSelect from "ui/pages/CustomCodes/components/HelperComponents/CustomCodeSelect";
import { useFetchCustomCodeComboPage } from "ui/pages/CustomCodes/hooks/useFetchCustomCodeComboPage";
import { ComboPageOption } from "ui/pages/CustomCodes/components/HelperComponents/CombosPageOption";
import EmptySalesPageView from "ui/pages/CustomCodes/components/HelperComponents/EmptySalesPageView";
import { checkIsArrayEmpty } from "@my-scoot/exly-react-component-lib";
import { useInfiniteListingOptions } from "features/Listings/utils/useInfiniteListingOptions";
import { LINK_TYPES } from "features/PaymentLinks/modules/CreatePaymentLinkModal/constants/CreatePaymentLinkModal.constants";

export const getGSTHelperText = (gst_status, gst_percentage) => {
  const gst_factor = 1 + (gst_percentage ?? 18) / 100;
  return [
    {
      text: "Price shown to the customer will be",
      factor: gst_status === constants.GST_STATUS.INCLUSIVE ? 1 : gst_factor,
    },
    {
      text: "Price excluding your GST will be",
      factor:
        gst_status === constants.GST_STATUS.INCLUSIVE ? 1 / gst_factor : 1,
    },
  ];
};

const FormContent = ({
  handleSubmit,
  form,
  values,
  isEdit,
  recordData,
  DateField,
  timeZone,
  placeholder,
  setPlaceHolder,
  offeringOptions,
  dynamicPagesOptions,
  shouldShowOfferingSelector,
  shouldShowDynamicLinkSelector,
  shouldShowPriceFields,
  shouldShowInternationalPricing,
  loadingInfiniteListings,
  lastElementInfiniteListingsRef,
  allDynamicPagesLoader,
  lastElementDynamicPagesRef,
  hideLayout,
  classes,
  gst_status,
  props,
  initialValues,
}) => {
  React.useEffect(() => {
    const dynamicPageCurrencies = [
      ...new Set(
        (values?.[LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS] || [])
          .map((id) =>
            dynamicPagesOptions.find(
              (p) => p.dynamic_link_id === id || p.value === id
            )
          )
          .filter(Boolean)
          .map((p) => p.currency)
          .filter(Boolean)
      ),
    ];
    const hasMixedCurrencies = dynamicPageCurrencies.length > 1;
    const isAllOfferings =
      values?.target_type === OFFER_TARGET_TYPE.ALL_OFFERINGS;
    const shouldRestrictToPercentage = hasMixedCurrencies || isAllOfferings;

    if (
      shouldRestrictToPercentage &&
      values[LTO_FORM_KEYS.OFFER_TYPE] === OFFER_TYPE.AMOUNT
    ) {
      form.change(LTO_FORM_KEYS.OFFER_TYPE, OFFER_TYPE.PERCENTAGE);
    }
  }, [
    values?.[LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS],
    values?.target_type,
    values?.[LTO_FORM_KEYS.OFFER_TYPE],
    dynamicPagesOptions,
    form,
  ]);

  const {
    [LTO_FORM_KEYS.LISTINGS]: selectedListings,
    [LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS]: selectedDynamicLinkUuids,
  } = values;

  const firstListing = offeringOptions.find(
    (o) => o.uuid === selectedListings?.[0]
  );
  const firstDynamicPageId = selectedDynamicLinkUuids?.[0];
  const firstDynamicPage = firstDynamicPageId
    ? dynamicPagesOptions.find((p) => p.dynamic_link_id === firstDynamicPageId)
    : null;

  const regionForCurrency = isEdit
    ? recordData?.region ?? values?.region
    : values?.region;
  const currencySymbol = isEdit
    ? getUserCurrencySymbol(
        regionForCurrency === constants.domestic_region &&
          !isInternationalCurrency()
          ? constants.inr_currency
          : constants.usd_currency
      ) ?? constants.inr_currency
    : (firstDynamicPage?.currency
        ? constants.CURRENCY_SYMBOL_MAP?.[firstDynamicPage?.currency]
        : firstListing?.currency
        ? constants.CURRENCY_SYMBOL_MAP?.[firstListing?.currency]
        : getUserCurrencySymbol(
            regionForCurrency === constants.domestic_region &&
              !isInternationalCurrency()
              ? constants.inr_currency
              : constants.usd_currency
          )) ?? constants.inr_currency;

  const selectedDynamicPages = selectedDynamicLinkUuids?.length
    ? dynamicPagesOptions.filter(
        (p) =>
          selectedDynamicLinkUuids.includes(p.value) ||
          selectedDynamicLinkUuids.includes(p.dynamic_link_id)
      )
    : [];

  const selectedOfferings = selectedListings?.length
    ? offeringOptions.filter((o) => selectedListings.includes(o.uuid))
    : [];

  const originalInrPrice =
    values?.target_type === OFFER_TARGET_TYPE.SPECIFIC_OFFERING
      ? Math.max(
          ...selectedOfferings.map((o) => o.price_per_head).filter(Boolean),
          0
        )
      : values?.target_type === OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE
      ? Math.max(
          ...selectedDynamicPages.map((p) => p.link_price).filter(Boolean),
          0
        )
      : null;

  const originalUsdPrice =
    values?.target_type === OFFER_TARGET_TYPE.SPECIFIC_OFFERING
      ? Math.max(
          ...selectedOfferings
            .map((o) => o.price_international)
            .filter(Boolean),
          0
        )
      : null;

  const dynamicPageCurrencies = [
    ...new Set(selectedDynamicPages.map((p) => p.currency).filter(Boolean)),
  ];
  const hasMixedCurrencies = dynamicPageCurrencies.length > 1;
  const isAllOfferings =
    values?.target_type === OFFER_TARGET_TYPE.ALL_OFFERINGS;
  const shouldRestrictToPercentage = hasMixedCurrencies || isAllOfferings;

  const handleTargetTypeChange = (e) => {
    form.reset({
      ...initialValues,
      [LTO_FORM_KEYS.TARGET_TYPE]: e,
    });
    setPlaceHolder(null);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={style.limitedContainer}>
        {!hideLayout && (
          <div className={style.limitedTitleContent}>
            <div className={style.limitedTitle}>
              {isEdit
                ? "Edit Limited-Time Offer"
                : "Create Limited-Time Offers"}
            </div>
            <div
              className={style.crossIcon}
              onClick={() => props.handleClick()}
            >
              &times;
            </div>
          </div>
        )}

        <div className={classes.offerfeildContainer}>
          <Field
            name={LTO_FORM_KEYS.TARGET_TYPE}
            component={ExlyDropdown}
            fullWidth
            size="small"
            label="Where do you want to run this offer?"
            disabled={isEdit}
            placeholder="Select target type"
            options={OFFER_TARGET_TYPE_OPTIONS}
            validate={(value) => !value && "Please select a target type"}
            onChange={handleTargetTypeChange}
          />

          {shouldShowOfferingSelector(values?.target_type) && (
            <Field
              name={LTO_FORM_KEYS.LISTINGS}
              component={ExlyOfferingSelect}
              label="Select Offerings *"
              mobileModalTitle="Select Offerings"
              placeholder="Select Offerings"
              showStatus
              fullWidth={true}
              multiple
              disabled={isEdit}
              onChange={(value) => {
                const ids = Array.isArray(value) ? value : [value];
                const first = offeringOptions.find((o) => o.uuid === ids[0]);
                if (first?.price_per_head) {
                  setPlaceHolder(
                    `Eg, ${Math.round(
                      first.price_per_head - first.price_per_head * 0.25
                    )} (original price - 25%)`
                  );
                }
              }}
              validate={(value) =>
                !value?.length && "Please select at least one offering"
              }
              options={offeringOptions}
              lastElementRef={lastElementInfiniteListingsRef}
              loading={loadingInfiniteListings}
            />
          )}

          {shouldShowDynamicLinkSelector(values?.target_type) && (
            <Field
              name={LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS}
              component={CustomCodeSelect}
              fullWidth
              size="small"
              options={dynamicPagesOptions}
              disabled={isEdit}
              loading={allDynamicPagesLoader}
              showStatus={true}
              multiple
              showChips
              label="Select Payment Pages"
              mobileModalTitle="Select Payment Pages"
              placeholder="Select Payment Pages"
              renderOption={(renderProps) => (
                <ComboPageOption
                  showCheckbox
                  showStatus
                  lastElementRef={lastElementDynamicPagesRef}
                  {...renderProps}
                />
              )}
              headerComponent={
                checkIsArrayEmpty(dynamicPagesOptions) &&
                !allDynamicPagesLoader && <EmptySalesPageView />
              }
              validate={(value) =>
                !value?.length
                  ? "Please select at least one payment page"
                  : undefined
              }
            />
          )}

          {values?.target_type === OFFER_TARGET_TYPE.SPECIFIC_OFFERING &&
            selectedListings?.[0] &&
            isInstallmentsPlanType(
              offeringOptions.find((o) => o.uuid === selectedListings[0])?.[
                PART_PAYMENT_FORM_KEYS.payment_plan_type
              ]
            ) && (
              <ExlyNote
                className="m-0"
                label={
                  <div>
                    <strong>Please note:</strong> This is a Part-payment enabled
                    offering. Limited-time offer will apply to the one-time
                    payment option only, not the part payment.
                  </div>
                }
              />
            )}

          <Field
            component={ExlyInput}
            labelClassName={classes.inputLabel}
            name="offerName"
            label="Offer Name *"
            placeholder="Eg, Flash Deal"
            fullWidth
            validate={validateOfferName}
          />

          <Field
            name={LTO_FORM_KEYS.OFFER_TYPE}
            component={ExlyDropdown}
            fullWidth
            size="small"
            label="Offer Type"
            placeholder="Select offer type"
            options={OFFER_TYPE_OPTIONS}
            validate={(value) => !value && "Please select offer type"}
            disabled={shouldRestrictToPercentage}
          />

          {shouldShowPriceFields(values) && (
            <>
              {values[LTO_FORM_KEYS.OFFER_TYPE] === OFFER_TYPE.PERCENTAGE ? (
                <Field
                  name={LTO_FORM_KEYS.PERCENTAGE_VALUE}
                  component={ExlyInput}
                  label={
                    <TooltipTitle
                      title="Offer Value"
                      description="Set the discount percentage for this offer"
                      className="!tw-mb-2"
                    />
                  }
                  type="number"
                  placeholder="Eg, 20"
                  fullWidth
                  suffix="%"
                  validate={validatePercentage}
                />
              ) : (
                <Field
                  name={LTO_FORM_KEYS.INR_PRICE}
                  component={ExlyPriceInput}
                  label={
                    <TooltipTitle
                      title="Offer Value"
                      description="Set the new price you want the customers to pay for this offer"
                      className="!tw-mb-2"
                    />
                  }
                  type="number"
                  placeholder={placeholder}
                  fullWidth
                  symbol={currencySymbol}
                  validate={(value) =>
                    validateDiscountPrice(value, originalInrPrice)
                  }
                />
              )}

              {values[LTO_FORM_KEYS.OFFER_TYPE] !== OFFER_TYPE.PERCENTAGE &&
              values[LTO_FORM_KEYS.INR_PRICE] > 0 &&
              values.region === constants.domestic_region &&
              form.getFieldState(LTO_FORM_KEYS.INR_PRICE)?.valid &&
              gst_status !== constants.GST_STATUS.INAPPLICABLE &&
              !isInternationalTimezone() ? (
                <div className={classes.gstInfo}>
                  <InfoIcon />
                  <div className={classes.infoContainer}>
                    {getGSTHelperText(
                      gst_status,
                      offeringOptions.find(
                        (o) => o.uuid === selectedListings?.[0]
                      )?.metadata?.gst_percent
                    ).map((item) => (
                      <div key={item.text}>
                        {item.text}&nbsp;
                        {currencySymbol}
                        {Math.round(values.INRPrice * item.factor * 100) / 100}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          )}
          {values[LTO_FORM_KEYS.OFFER_TYPE] !== OFFER_TYPE.PERCENTAGE &&
            shouldShowInternationalPricing(values) && (
              <>
                <div className={classes.salescheckbox}>
                  <Field
                    id="USDCheckbox"
                    name={LTO_FORM_KEYS.USD_CHECKBOX}
                    type="checkbox"
                    component={Checkbox}
                    size="large"
                    onChange={(e) =>
                      form.change(LTO_FORM_KEYS.USD_CHECKBOX, e.target.checked)
                    }
                  />
                  <label
                    htmlFor="USDCheckbox"
                    className={classes.checkboxtitle}
                  >
                    I want to set a different price for International clients
                  </label>
                </div>

                {values[LTO_FORM_KEYS.USD_CHECKBOX] && (
                  <>
                    <Field
                      name={LTO_FORM_KEYS.USD_PRICE}
                      component={ExlyPriceInput}
                      placeholder="Enter price"
                      type="number"
                      fullWidth
                      symbol="$"
                      validate={(value) =>
                        validateDiscountPriceInternational(
                          value,
                          originalUsdPrice
                        )
                      }
                    />

                    {values[LTO_FORM_KEYS.USD_PRICE] > 0 &&
                    form.getFieldState(LTO_FORM_KEYS.USD_PRICE)?.valid &&
                    gst_status !== constants.GST_STATUS.INAPPLICABLE ? (
                      <div className={classes.gstInfo}>
                        <InfoIcon />
                        <div className={classes.infoContainer}>
                          {getGSTHelperText(gst_status).map((item) => (
                            <div key={item.text}>
                              {item.text}&nbsp;
                              {constants.usd_currency}
                              {Math.round(
                                values[LTO_FORM_KEYS.USD_PRICE] *
                                  item.factor *
                                  100
                              ) / 100}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </>
            )}

          <TooltipTitle
            title="Timer Type"
            description="Fixed — The timer runs on a specific end date and time you set. Evergreen — The timer starts when a customer first visits the page and counts down from a duration you set (minutes/hours/days)."
          />
          <Field
            name={LTO_FORM_KEYS.TIMER_TYPE}
            component={ExlyDropdown}
            fullWidth
            size="small"
            placeholder="Select timer type"
            options={TIMER_TYPE_OPTIONS}
            validate={(value) => !value && "Please select timer type"}
          />

          {values[LTO_FORM_KEYS.TIMER_TYPE] === TIMER_TYPE.EVERGREEN ? (
            <>
              <TooltipTitle
                title="Offer Duration"
                description="Set how long the offer timer will count down from when a customer first visits the page"
              />
              <div className="tw-flex tw-w-full tw-gap-3">
                <div className="tw-flex-1 tw-min-w-0">
                  <Field
                    name={LTO_FORM_KEYS.EVERGREEN_DURATION_VALUE}
                    component={ExlyInput}
                    type="number"
                    min="1"
                    label="Duration *"
                    placeholder="Enter duration"
                    fullWidth
                    validate={(value) => !value && "Please enter duration"}
                  />
                </div>
                <div className="tw-shrink-0 tw-w-[130px] -tw-mt-[0.2rem]">
                  <Field
                    name={LTO_FORM_KEYS.EVERGREEN_DURATION_UNIT}
                    component={ExlyDropdown}
                    options={DURATION_UNITS}
                    fullWidth
                    size="small"
                    label="Unit"
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <TooltipTitle
                title="Discount Period"
                description="Select a time period for which the discount will be valid for all customers"
              />

              <DurationPickerField
                label="Offer Start Date *"
                datePickerProps={{
                  name: "start_date",
                  validate: (value) => !value && "Required",
                  parse: (date) => {
                    if (isEdit && recordData.status !== 0) return;

                    const todaysDate = new Date();

                    if (
                      date?.getDate() === todaysDate?.getDate() &&
                      date?.getFullYear() === todaysDate?.getFullYear() &&
                      date?.getMonth() === todaysDate?.getMonth()
                    ) {
                      const time = DateField(todaysDate, "fetchTime");
                      form.change("start_time", time?.time.toLowerCase());
                      form.change(
                        "start_time_international",
                        time?.time.toLowerCase()
                      );
                    }

                    const formattedDate = moment(new Date(date)).format(
                      "YYYY-MM-DD"
                    );

                    values?.[LTO_FORM_KEYS.LISTINGS]?.length > 0 &&
                      form.change("start_date_international", formattedDate);

                    return formattedDate;
                  },
                }}
                timePickerProps={{
                  name: "start_time",
                  parse: (value) => {
                    form.change("start_time_international", value);
                    return value;
                  },
                  uniqueId: `offer_start_time`,
                  validate: composeValidators(required, (value) => {
                    const { start_date } = values;
                    const isPast = moment(
                      `${start_date} ${value}`,
                      `${DEFAULT_DATE_FORMAT} ${DEFAULT_TIME_FORMAT_12}`
                    ).isBefore(moment());
                    if (isPast) return "Please pick time in future";
                  }),
                }}
              />

              <DurationPickerField
                label="Offer End Date *"
                datePickerProps={{
                  name: "end_date",
                  validate: (value) => validateEndDate(value, values),
                  parse: (value) => {
                    const formattedDate = moment(new Date(value)).format(
                      "YYYY-MM-DD"
                    );

                    values?.[LTO_FORM_KEYS.LISTINGS]?.length > 0 &&
                      form.change("end_date_international", formattedDate);

                    return formattedDate;
                  },
                }}
                timePickerProps={{
                  name: "end_time",
                  uniqueId: `offer_end_time`,
                  parse: (value) => {
                    form.change("end_time_international", value);
                    return value;
                  },
                }}
              />
              <div className={style.termsText}>
                <p>{`** Default Timezone is ${timeZone}`}</p>
              </div>
            </>
          )}
        </div>

        {!hideLayout && (
          <div className={style.offerButton}>
            <button type="submit" className={style.createButton}>
              {isEdit ? "Update Offer" : "Create Offer"}
            </button>
            {!isEdit && (
              <button className={style.clearButton} onClick={form.reset}>
                Clear
              </button>
            )}
          </div>
        )}
      </div>
    </form>
  );
};

export const LimitedOfferModalContent = memo(
  React.forwardRef((props, ref) => {
    const classes = useStyles({ is_desktop });

    const { showShareModal = true, forceShowForm = false } = props;

    const {
      data: allDynamicPagesData,
      loading: allDynamicPagesLoader,
      lastElementRef: lastElementDynamicPagesRef,
    } = useFetchCustomCodeComboPage({
      initialData: {
        isActive: true,
      },
      paymentLinkType: [LINK_TYPES.CUSTOM_LINK, LINK_TYPES.COMBO_LINK],
    });

    const {
      options: infiniteListingOptions,
      loading: loadingInfiniteListings,
      lastElementRef: lastElementInfiniteListingsRef,
    } = useInfiniteListingOptions({
      formatOptions: (options) =>
        options?.map((listing) => ({
          ...listing,
          id: listing.uuid,
          label: listing.title,
          value: listing.uuid,
        })),
      isOfferingVariantsEnabled: false,
      hideMerchVariantsEnabledListing: true,
    });

    const {
      isEdit,
      initialValues,
      recordData,
      firstLimitedOffer,
      listingUuid,
      limitedOfferCreative,
      handleCreateOffer,
      DateField,
      selectedListing,
      shareUrl,
      shouldShowOfferingSelector,
      shouldShowDynamicLinkSelector,
      shouldShowPriceFields,
      shouldShowInternationalPricing,
      dynamicPagesOptions,
      offeringOptions,
    } = useLimitedTimeOffer({
      allDynamicPagesData,
      infiniteListingOptions,
      ...props,
    });

    const timeZone = timezones.all_timezones.find(
      (o) => o.value === getUserTimezone()
    ).label;

    const { gst_status, hideLayout } = props;
    const [placeholder, setPlaceHolder] = React.useState(null);

    const formRef = React.useRef(null);

    React.useImperativeHandle(ref, () => ({
      triggerSave() {
        if (!formRef.current) return;

        const { submit } = formRef.current;
        const { values: formState, valid } = formRef.current.getState();

        submit();
        return valid ? handleCreateOffer(formState).then((res) => res) : {};
      },
    }));

    return (
      <>
        {forceShowForm || !limitedOfferCreative ? (
          <Form
            initialValues={initialValues}
            onSubmit={!hideLayout ? handleCreateOffer : () => {}}
          >
            {({ handleSubmit, values, form }) => {
              formRef.current = form;
              return (
                <FormContent
                  handleSubmit={handleSubmit}
                  form={form}
                  values={values}
                  isEdit={isEdit}
                  recordData={recordData}
                  handleCreateOffer={handleCreateOffer}
                  DateField={DateField}
                  timeZone={timeZone}
                  placeholder={placeholder}
                  setPlaceHolder={setPlaceHolder}
                  offeringOptions={offeringOptions}
                  dynamicPagesOptions={dynamicPagesOptions}
                  shouldShowOfferingSelector={shouldShowOfferingSelector}
                  shouldShowDynamicLinkSelector={shouldShowDynamicLinkSelector}
                  shouldShowPriceFields={shouldShowPriceFields}
                  shouldShowInternationalPricing={
                    shouldShowInternationalPricing
                  }
                  loadingInfiniteListings={loadingInfiniteListings}
                  lastElementInfiniteListingsRef={
                    lastElementInfiniteListingsRef
                  }
                  allDynamicPagesLoader={allDynamicPagesLoader}
                  lastElementDynamicPagesRef={lastElementDynamicPagesRef}
                  hideLayout={hideLayout}
                  classes={classes}
                  gst_status={gst_status}
                  props={props}
                  initialValues={initialValues}
                />
              );
            }}
          </Form>
        ) : (
          <LimitedOfferShare
            setShowModal={limitedOfferCreative && showShareModal}
            firstOffer={firstLimitedOffer}
            listing={selectedListing}
            uid={listingUuid}
            shareUrl={shareUrl}
            showBroadcast
          />
        )}
      </>
    );
  })
);
